/**
 * Provider-agnostic auth hooks.
 *
 * All components import from here instead of directly from @frontegg/react.
 * This is the single abstraction layer for auth — when the provider changes,
 * only this file changes. The 15+ consumer files stay untouched.
 *
 * Provider chain:
 *   1. OIDC (custom domains with oidc_config — no Frontegg involved)
 *   2. Frontegg (main domain and non-OIDC custom domains)
 */
import { useAuth as useFronteggAuth, useAuthActions as useFronteggAuthActions, useTenantsState as useFronteggTenantsState } from '@frontegg/react';
import { usePliableAuth, usePliableAuthActions, usePliableTenantsState } from '../contexts/PliableAuthContext';
import AuthService from '../services/auth.service';

/** Normalized user shape — provider-agnostic */
export interface AppUser {
  id: string;
  sub: string;
  name: string;
  email: string;
  tenantId: string;
  tenantIds: string[];
  roles: Array<{ key: string; name: string }>;
  accessToken: string;
  profilePictureUrl: string;
  verified: boolean;
  mfaEnrolled: boolean;
  activatedForTenant: boolean;
  tenants: Array<{ tenantId: string; name: string }>;
  customClaims: Record<string, any>;
  permissions: string[];
}

export interface AppAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | undefined;
}

export interface AppAuthActions {
  logout: () => void;
  switchTenant: (params: { tenantId: string }) => void;
}

export interface AppTenantsState {
  tenants: Array<{ tenantId: string; name: string }>;
  loading: boolean;
}

function buildUserFromRaw(raw: any): AppUser {
  return {
    id: raw.id || raw.sub || '',
    sub: raw.sub || raw.id || '',
    name: raw.name || '',
    email: raw.email || '',
    tenantId: raw.tenantId || '',
    tenantIds: raw.tenantIds || [raw.tenantId],
    roles: (raw.roles || []).map((r: any) =>
      typeof r === 'string' ? { key: r, name: r } : r
    ),
    accessToken: raw.accessToken || '',
    profilePictureUrl: raw.profilePictureUrl || '',
    verified: raw.verified ?? true,
    mfaEnrolled: raw.mfaEnrolled ?? false,
    activatedForTenant: raw.activatedForTenant ?? true,
    tenants: raw.tenants || [],
    customClaims: raw.customClaims || {},
    permissions: raw.permissions || [],
  };
}

// Detect OIDC mode once at module level — hooks can't be called conditionally,
// but we can branch which hooks module to call based on this flag.
const isPliableAuth = AuthService.getInstance().oidcEnabled;

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useAppAuth(): AppAuthState {
  // In OIDC mode, use the lightweight OIDC context (no Frontegg).
  // In Frontegg mode, use Frontegg hooks with handoff JWT fallback.
  if (isPliableAuth) {
    return usePliableAppAuth();
  }
  return useFronteggAppAuth();
}

function usePliableAppAuth(): AppAuthState {
  const pliable = usePliableAuth();
  if (pliable.isAuthenticated && pliable.user) {
    return {
      isAuthenticated: true,
      isLoading: false,
      user: buildUserFromRaw(pliable.user),
    };
  }
  return { isAuthenticated: false, isLoading: pliable.isLoading, user: undefined };
}

function useFronteggAppAuth(): AppAuthState {
  const frontegg = useFronteggAuth();
  const authService = AuthService.getInstance();
  const authState = authService.getAuthState();

  // Frontegg authenticated
  if (frontegg.isAuthenticated && frontegg.user) {
    return frontegg as any;
  }

  // Handoff JWT fallback (Safari custom domain)
  if (authState && authService.handoffToken) {
    return {
      isAuthenticated: true,
      isLoading: false,
      user: buildUserFromRaw({
        ...authState,
        id: authState.sub,
        tenantId: authState.tenant_id,
        tenantIds: [authState.tenant_id],
        roles: authState.roles,
        accessToken: authState.access_token,
        profilePictureUrl: authState.profile_img_url,
      }),
    };
  }

  return frontegg as any;
}

export function useAppAuthActions(): AppAuthActions {
  if (isPliableAuth) {
    return usePliableAppAuthActions();
  }
  return useFronteggAppAuthActions();
}

function usePliableAppAuthActions(): AppAuthActions {
  return usePliableAuthActions();
}

function useFronteggAppAuthActions(): AppAuthActions {
  const fronteggActions = useFronteggAuthActions();
  const authService = AuthService.getInstance();

  // Custom domain handoff: wrap logout to clean up local state
  if (authService.handoffToken && authService.isCustomDomain) {
    return {
      logout: () => {
        sessionStorage.removeItem('plb_auth_state');
        sessionStorage.removeItem('plb_handoff_token');
        authService.handoffToken = null;
        authService.plbAuthState = null;
        try { fronteggActions.logout(); } catch (e) { /* no valid session */ }
        window.location.replace('/login');
      },
      switchTenant: fronteggActions.switchTenant,
    };
  }

  return fronteggActions;
}

export function useAppTenantsState(): AppTenantsState {
  if (isPliableAuth) {
    return usePliableAppTenantsState();
  }
  return useFronteggAppTenantsState();
}

function usePliableAppTenantsState(): AppTenantsState {
  return usePliableTenantsState();
}

function useFronteggAppTenantsState(): AppTenantsState {
  const frontegg = useFronteggTenantsState();
  const authService = AuthService.getInstance();
  const authState = authService.getAuthState();

  if (frontegg.tenants && frontegg.tenants.length > 0) {
    return frontegg as any;
  }

  if (authState && authService.handoffToken) {
    return {
      tenants: authState.tenants?.length ? authState.tenants : [{
        tenantId: authState.tenant_id,
        name: authState.tenant_name || authState.tenant_id,
      }],
      loading: false,
    };
  }

  return frontegg as any;
}
